import React from 'react'
import { Tabs, Tab } from '@mui/material'

import FileSection from '../FileSection'
import Editor, {
  isEmpty,
  replaceWithBr
} from '@SonarCX/components/organisms/LexicalEditor'
import TaskNote from '@SonarCX/components/molecules/TaskNote'
import EmailSection from '@SonarCX/components/molecules/ChatModals/TaskModal/EmailSection'

import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import useStyles from './styles'

const DESCRIPTION = 0
const NOTES = 1
const EMAILS = 2

const TaskDescription = ({
  values,
  handleChange,
  MAX_DESCRIPTION_LENGTH,
  setValues,
  setModifiedTask,
  canEdit,
  setChanged,
  onBlur,
  canSwitch
}) => {
  const { isSmall } = useBreakpoint()
  const { setAction, setReason, setV2 } = useAreYouSure()

  const [internalValue, setInternalValue] = React.useState(
    replaceWithBr(values.content ?? '')
  )

  const [tab, setTab] = React.useState(DESCRIPTION)

  const classes = useStyles()

  const handleOnChange = value => {
    if (isEmpty(value)) {
      handleChange('content')('')
    } else {
      handleChange('content')(value)
    }
    setInternalValue(value)
  }

  const handleTabSwitch = React.useCallback(
    tab => {
      const action = () => setTab(tab)

      if (canSwitch || tab === NOTES) {
        action()
        return
      }

      setReason('You have unsaved changes')
      setAction(action)
      setV2(true)
    },
    [canSwitch, setReason, setAction, setV2]
  )

  const triggerSave = React.useCallback(() => {
    onBlur && onBlur()
    setChanged(true)
  }, [onBlur, setChanged])

  return (
    <div className={classes.root}>
      <Tabs
        value={tab}
        onChange={(_, val) => handleTabSwitch(val)}
        className={classes.tabs}
        centered
      >
        <Tab label='Description' classes={{ root: classes.tab }} />

        <Tab label='Notes' classes={{ root: classes.tab }} />

        <Tab label='Emails' classes={{ root: classes.tab }} />
      </Tabs>

      <div className={classes.body}>
        {tab === DESCRIPTION && (
          <>
            <Editor
              disableImages
              maxLength={3000}
              enabled={canEdit}
              showSpeechToText={false}
              value={internalValue}
              onChange={handleOnChange}
              onBlur={() => onBlur && onBlur()}
              placeholder={
                canEdit
                  ? 'Enter task details'
                  : "You don't have permission to edit this task"
              }
              className={classes.editor}
            />

            <div className={classes.count}>
              {values.content?.length ?? 0} / {MAX_DESCRIPTION_LENGTH}
            </div>
          </>
        )}

        {tab === NOTES && (
          <TaskNote
            values={values}
            setValues={setValues}
            setChanged={setChanged}
            triggerSave={triggerSave}
          />
        )}

        {tab === EMAILS && (
          <div style={{ height: 'auto' }}>
            <EmailSection
              values={values}
              setValues={setValues}
              triggerSave={triggerSave}
            />
          </div>
        )}

        {canEdit && (
          <div style={{ height: 'auto' }}>
            <FileSection
              setChanged={val => {
                if (val) {
                  onBlur && onBlur()
                }
                setChanged(val)
              }}
              setValues={setValues}
              values={values}
              setModifiedTask={setModifiedTask}
              onMobile={isSmall}
              multi
              enablePastingFiles
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default TaskDescription
